import React, { useRef, useState, useEffect } from 'react';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import { Dropdown, MenuProps, Button, Space } from 'antd';


// Custom Icons
const PlayIcon = () => (
    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="20" cy="20" r="20" fill="var(--ielts-accent)"/>
    <path d="M27 20L15.75 26.4952L15.75 13.5048L27 20Z" fill="var(--ielts-bg)"/>
    </svg>
);

const PauseIcon = () => (
    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="20" cy="20" r="20" fill="var(--ielts-accent)"/>
    <rect x="14" y="12" width="4" height="16" rx="1" fill="var(--ielts-bg)"/>
    <rect x="22" y="12" width="4" height="16" rx="1" fill="var(--ielts-bg)"/>
    </svg>
);

const VolumeIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M11 5L6 9H2V15H6L11 19V5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M15.54 8.46C16.4774 9.39764 17.004 10.6692 17.004 11.995C17.004 13.3208 16.4774 14.5924 15.54 15.53" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M19.07 4.93C20.9447 6.80527 21.9979 9.34836 21.9979 12C21.9979 14.6516 20.9447 17.1947 19.07 19.07" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);


const DownOutlinedIcon = () => (
    <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);


import useTranslation from 'next-translate/useTranslation';

interface SpeakerCheckProps {
  description: string;
  audioSrc: string; // URL for the sample sound
  onPass?: () => void;
  noBackground?: boolean; // Remove background and padding
  hideTitle?: boolean;
}

const SpeakerCheck: React.FC<SpeakerCheckProps> = ({
  description,
  audioSrc,
  onPass,
  noBackground = false,
  hideTitle = false,
}) => {
  const { t } = useTranslation('ielts');
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [deviceLabel, setDeviceLabel] = useState<string>(''); // Initialize empty or with a key if needed, but for display we use t()

  // Initialize label with translated default
  useEffect(() => {
    setDeviceLabel(t('TestMicrophone.default_headphones'));
  }, [t]);

  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio(audioSrc);
    } else {
      audioRef.current.src = audioSrc;
    }

    const audio = audioRef.current;

    const handleLoadedMetadata = () => {
      if (audio.duration !== Infinity && !Number.isNaN(audio.duration)) {
        setDuration(audio.duration);
      }
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
      if (onPass) onPass();
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.pause();
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [audioSrc, onPass]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play().catch((e) => console.error('Play error:', e));
    }
    setIsPlaying(!isPlaying);
  };

  const handleSliderChange = (val: number | number[]) => {
    const audio = audioRef.current;
    if (!audio) return;

    const newTime = Array.isArray(val) ? val[0] : val;
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  // Dummy device list
  const items: MenuProps['items'] = [
    {
      label: t('TestMicrophone.default_headphones'),
      key: '1',
      onClick: () => setDeviceLabel(t('TestMicrophone.default_headphones')),
    },
    {
      label: t('TestMicrophone.speaker_external'),
      key: '2',
      onClick: () => setDeviceLabel(t('TestMicrophone.speaker_external')),
    },
  ];

  return (
    <div
      className={noBackground ? '' : 'p-6 rounded-2xl mb-6'}
      style={noBackground ? {} : { background: 'var(--ielts-surface-muted)' }}
    >
      {!hideTitle && (
        <h3 className="text-xl font-bold mb-2" style={{ color: 'var(--ielts-fg)' }}>
          {t('TestMicrophone.speaker_check_title')}
        </h3>
      )}
      <p className="mb-4 text-sm" style={{ color: 'var(--ielts-fg-secondary)' }}>
        {description}
      </p>

      <div
        className="flex items-center gap-4 p-2 pl-3 pr-4 rounded-xl shadow-sm border h-[64px]"
        style={{
          background: 'var(--ielts-control)',
          borderColor: 'var(--ielts-line)',
        }}
      >
        <button
          type="button"
          onClick={togglePlay}
          className="focus:outline-none hover:opacity-80 transition-opacity"
        >
          {isPlaying ? <PauseIcon /> : <PlayIcon />}
        </button>

        <div className="flex-1 mx-2 relative top-[2px]">
          <Slider
            min={0}
            max={duration || 100}
            value={currentTime}
            onChange={handleSliderChange}
            trackStyle={{ backgroundColor: 'var(--ielts-accent)', height: 6 }}
            handleStyle={{
              borderColor: 'var(--ielts-accent)',
              height: 14,
              width: 14,
              marginTop: -4,
              backgroundColor: 'var(--ielts-accent)',
              boxShadow: 'none',
              opacity: 1,
            }}
            railStyle={{
              backgroundColor: 'var(--ielts-line)',
              height: 6,
            }}
          />
        </div>

        <span
          className="font-medium w-12 text-right text-sm"
          style={{ color: 'var(--ielts-fg-secondary)' }}
        >
          00:
          {Math.floor(duration || 0)
            .toString()
            .padStart(2, '0')}
        </span>

        <div
          className="border-l pl-4 ml-2 h-8 items-center hidden md:flex"
          style={{ borderColor: 'var(--ielts-line)' }}
        >
          <Dropdown menu={{ items }} trigger={['click']}>
            <Button
              type="text"
              className="flex items-center hover:opacity-80 p-0 h-auto"
              style={{ color: 'var(--ielts-fg-secondary)' }}
            >
              <Space size={8}>
                <VolumeIcon />
                <span className="max-w-[140px] truncate block text-sm">{deviceLabel}</span>
                <DownOutlinedIcon />
              </Space>
            </Button>
          </Dropdown>
        </div>
      </div>
    </div>
  );
};
export default SpeakerCheck;
